import React, { useState } from 'react';
import { Box, Button, InputAdornment, Stack, TablePagination, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Search } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { getAuthHeaders, makeApiCall } from '../../utils/apiConfig';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { tokens } from '../../theme/tokens';
import { EmptyState, InlineMessage, ResponsiveRows, SectionCard, StatusChip } from '../ui';
import type { StatusTone } from '../ui';

/** `GET /store/sku-crosswalk/queue` (flag `catalog_queue_window`): the store's view of a staged row. */
export type QueueState = 'pending' | 'remediating' | 'review' | 'approved' | 'promoted' | 'rejected';

export interface CatalogQueueItem {
  stagedProductId: string;
  queueState: QueueState;
  openGaps: string[];
  lastUpdated: string | null;
  name: string;
  brand: string;
  brandId: string;
  model: string;
  sku: string;
  mappings: Array<{ id: string; storeSku?: string; storeBarcode?: string; condition: string; integrationId: string | null }>;
}

export interface CatalogQueueResponse {
  items: CatalogQueueItem[];
  total: number;
  limit: number;
  offset: number;
  byBrand: Array<{ brandId: string; brand: string; count: number }>;
  byState: Record<QueueState, number>;
}

export const QUEUE_STATE_TONE: Record<QueueState, StatusTone> = {
  pending: 'info',
  remediating: 'caution',
  review: 'caution',
  approved: 'positive',
  promoted: 'positive',
  rejected: 'neutral',
};

/** Gap ids the pipeline reports; anything else falls back to the id itself. */
const KNOWN_GAPS = new Set([
  'sku', 'upc', 'category', 'productType', 'price', 'weight', 'photo', 'shortDescription', 'longDescription', 'features',
  'fujifilmCatalog', 'verificationError', 'identifierReview', 'syntheticSku', 'aiContentPending', 'documentation', 'videos', 'inTheBox',
]);

type StateFilter = 'all' | QueueState;
const STATE_FILTERS: StateFilter[] = ['all', 'remediating', 'review', 'pending', 'approved'];

const errorMessage = async (response: Response, fallback: string): Promise<string> => {
  const body = await response.json().catch(() => ({}));
  return body?.error || fallback;
};

const COLUMNS = [
  { key: 'product', header: 'product', width: 'minmax(0,1.6fr)' },
  { key: 'yours', header: 'yours', width: 'minmax(0,1fr)', mobileLabel: true },
  { key: 'state', header: 'state', width: '150px', mobileLabel: true },
  { key: 'gaps', header: 'gaps', width: 'minmax(0,1.2fr)', mobileLabel: true },
  { key: 'updated', header: 'updated', width: '120px', mobileLabel: true },
];

/**
 * The Catalog queue tab of the SKU Crosswalk page (flag `catalog_queue_window`): the staged
 * products the store's own crosswalk rows resolve to, with their state and the data still
 * missing. Read-only in this slice; fast-track and proposals come behind their own flags.
 */
export const CatalogQueueCard: React.FC = () => {
  const { t, i18n } = useAppTranslation();
  const [stateFilter, setStateFilter] = useState<StateFilter>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['skuCrosswalkQueue', stateFilter, search, page, rowsPerPage],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: String(rowsPerPage),
        offset: String(page * rowsPerPage),
        ...(search ? { search } : {}),
        ...(stateFilter !== 'all' ? { state: stateFilter } : {}),
      });
      const response = await makeApiCall(`/store/sku-crosswalk/queue?${params}`, { headers: await getAuthHeaders() });
      if (!response.ok) throw new Error(await errorMessage(response, t('store.skuCrosswalk.queue.loadError')));
      return response.json() as Promise<CatalogQueueResponse>;
    },
  });

  const items = data?.items || [];
  const total = data?.total || 0;
  const byState = data?.byState;
  const byBrand = data?.byBrand || [];
  const inQueue = byState ? Object.values(byState).reduce((a, b) => a + b, 0) : 0;
  const waiting = (byState?.remediating || 0) + (byState?.pending || 0);
  const underReview = byState?.review || 0;

  const gapLabel = (gap: string) => (KNOWN_GAPS.has(gap) ? t(`store.skuCrosswalk.queue.gaps.${gap}`) : gap);
  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isFinite(d.getTime()) ? d.toLocaleDateString(i18n.language) : '—';
  };

  const columns = COLUMNS.map((c) => ({ ...c, header: t(`store.skuCrosswalk.queue.columns.${c.header}`) }));

  const renderCell = (item: CatalogQueueItem, key: string): React.ReactNode => {
    switch (key) {
      case 'product':
        return (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>{item.name || item.model || item.sku}</Typography>
            <Typography variant="caption" sx={{ color: tokens.ink.faint }} noWrap component="div">
              {[item.brand, item.sku].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
        );
      case 'yours':
        return (
          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
            {item.mappings.map((m) => m.storeSku || m.storeBarcode).filter(Boolean).join(', ') || '—'}
          </Typography>
        );
      case 'state':
        return <StatusChip label={t(`store.skuCrosswalk.queue.state.${item.queueState}`)} tone={QUEUE_STATE_TONE[item.queueState]} />;
      case 'gaps':
        return (
          <Typography variant="body2" color={item.openGaps.length ? 'text.primary' : 'text.secondary'}>
            {item.openGaps.length ? item.openGaps.map(gapLabel).join(', ') : t('store.skuCrosswalk.queue.noGaps')}
          </Typography>
        );
      default:
        return <Typography variant="body2" color="text.secondary">{formatDate(item.lastUpdated)}</Typography>;
    }
  };

  return (
    <SectionCard
      dense
      title={t('store.skuCrosswalk.queue.title', { count: inQueue })}
      actions={
        <TextField
          size="small"
          placeholder={t('store.skuCrosswalk.queue.searchPlaceholder')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: { xs: 160, sm: 260 } }}
        />
      }
    >
      <Box sx={{ px: '20px', py: 1.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {t('store.skuCrosswalk.queue.coverage', { count: inQueue, waiting, review: underReview })}
        </Typography>
        <Typography variant="body2" color="text.secondary">{t('store.skuCrosswalk.queue.help')}</Typography>
        {byBrand.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            {byBrand.map((b) => (
              <StatusChip key={b.brandId || b.brand} size="small" tone="neutral" label={`${b.brand} · ${b.count}`} />
            ))}
          </Stack>
        )}
      </Box>

      {error && (
        <Box sx={{ px: '20px', pb: 1.5 }}>
          <InlineMessage severity="error">
            {(error as Error).message}{' '}
            <Button size="small" onClick={() => refetch()}>{t('store.skuCrosswalk.unmapped.retry')}</Button>
          </InlineMessage>
        </Box>
      )}

      <Box sx={{ px: '20px', py: 1, borderTop: `1px solid ${tokens.line.hairline}` }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={stateFilter}
          onChange={(_e, next: StateFilter | null) => {
            if (!next) return;
            setStateFilter(next);
            setPage(0);
          }}
          aria-label={t('store.skuCrosswalk.queue.filterAria')}
        >
          {STATE_FILTERS.map((s) => (
            <ToggleButton key={s} value={s}>
              {s === 'all'
                ? t('store.skuCrosswalk.queue.filterAll', { count: inQueue })
                : `${t(`store.skuCrosswalk.queue.state.${s}`)} (${byState?.[s] ?? 0})`}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <ResponsiveRows
        columns={columns}
        headerCellSx={{ textTransform: 'none', letterSpacing: 0, fontSize: 13, fontWeight: 600, color: tokens.ink.body }}
        rows={items}
        getRowKey={(item) => item.stagedProductId}
        renderCell={renderCell}
        isLoading={isLoading}
        emptyState={
          <EmptyState
            size="compact"
            title={search || stateFilter !== 'all' ? t('store.skuCrosswalk.queue.noMatches') : t('store.skuCrosswalk.queue.emptyTitle')}
            description={search || stateFilter !== 'all' ? undefined : t('store.skuCrosswalk.queue.emptyDescription')}
          />
        }
      />
      {total > 0 && (
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
          onPageChange={(_e, next) => setPage(next)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      )}
    </SectionCard>
  );
};

export default CatalogQueueCard;
